import { max, min_value } from '@vee-validate/rules';
import { defineRule } from 'vee-validate';
import { pDecimalValidationTest } from '@/components/mixins/ValidatedInput.js';

defineRule('unique-value', (value, { isDuplicated }) => {
    if (!isDuplicated(value)) return true;
    return `Value cannot be duplicated.`;
});

defineRule('required-if-new-barcode', (value, { isNewBarcode, rowData }) => {
    if (!isNewBarcode(rowData)) {
        return true;
    }
    if (value) return true;
    return `This field is required for new barcodes.`;
});

export default {
    data() {
        return {
            existingBarcodes: [],
            gmConfiguration: {
                type: 'gm',
                label: 'General Merchandise',
                importModes: [
                    {value:'add', text:'Add New (Skip when Barcode already present)'},
                    {value:'update', text:'Update (Skip when Barcode not present, update when found)'},
                    {value:'addAndUpdate', text:'Add and Update'}
                ],                
                sampleNotes: [
                    'Rows with GM Barcode Xrefs that already exist will still be imported but the duplicate xref will be skipped',
                    'Variant1 is for product size and Variant2 is for product color',
                    'Brand names not found will be added',
                    'Catalog names not found will not be associated to the product',
                    'Updates ignore the following fields: GmBarcodeXref, Variant1, Variant2, Serialization',
                    'Updates will ignore the following fields if they are left blank: DCC, ItemComment, Cost, Retail, Description, BrandName, WebsiteDisplayName, WebsiteInventoryTolerance, WebsiteLongDescription, ShowOnlineFlag, WebCatalog',
                    'Updates will ignore barcodes that are not GM products'
                ],
                importNotes: () =>  { return this.textBookImportNotes},
                fields: [
                    {
                        key: 'Barcode',
                        label: 'Barcode',
                        hasWarningCalculator: value => {
                            // Misplaced `||` previously short-circuited the
                            // duplicate-barcode check inside max()'s params.
                            return (
                                !this.isEmpty(value) &&
                                (max(value, { length: 128 }) !== true ||
                                    this.isDuplicatedOnImportBarcode())
                            );
                        },
                        warningDefault: '',
                        rules: {
                            'unique-value': {
                                isDuplicated: this.isDuplicatedOnImportBarcode
                            },
                            max: { length: 128 }
                        },
                        samples: ['"2235467890161"','"83266024874"','"45171766836"']
                    },
                    {
                        key: 'GmBarcodeXref',
                        label: 'GM Barcode Xref',
                        hasWarningCalculator: value => {
                            return max(value, { length: 500 }) !== true;
                        },
                        warningDefault: '',
                        rules: {
                            max: { length: 500 }
                        },
                        samples: ['','','"0097522|048419315414|048419307129|048419205494"']
                    },
                    {
                        key: 'DCC',
                        label: 'DCC',
                        selectOptionsFilter: () => {return  1;},
                        hasErrorCalculator: (value, row) => {
                            if (this.isEmpty(value)) {
                                return this.isNewBarcode(row);
                            }
                            return !this.matchesExistingObject(value, true);
                        },
                        rules: {
                            'required-if-new-barcode': {
                                isNewBarcode: this.isNewBarcode
                            },
                            min_value: { min: 0 }
                        },
                        addRowDataToRule: 'required-if-new-barcode',
                        samples: ['"202030"','"202020"','"202010"'],
                        comparisonProperty: 'dccNumbers',
                        dataType: 'dcc'
                    },
                    {
                        key: 'ItemComment',
                        label: 'Item Comment',
                        hasWarningCalculator: value => {
                            if (this.isEmpty(value)) {
                                return false;
                            }
                            return max(value, { length: 200 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:200',
                        samples: ['"SP ODR DEBBI BUMPOUS"']
                    },
                    {
                        key: 'Cost',
                        label: 'Cost',
                        hasWarningCalculator: value => {
                            return (
                                value &&
                                (!pDecimalValidationTest(value, {
                                    precision: '10',
                                    scale: '2'
                                })
                                || value < 0)
                            );
                        },
                        warningDefault: null,
                        rules: 'p-decimal:10,2|min_value:0',
                        samples: [10.55,1.64,17.5],
                        convertToNumber: true
                    },
                    {
                        key: 'Retail',
                        label: 'Retail',
                        hasErrorCalculator: (value, row) => {
                            if (this.isEmpty(value)) {
                                return this.isNewBarcode(row);
                            }
                            return (
                                value &&
                                (!pDecimalValidationTest(value, {
                                    precision: '10',
                                    scale: '2'
                                })
                                || value < 0)
                            );
                        },
                        rules: {
                            'required-if-new-barcode': {
                                isNewBarcode: this.isNewBarcode
                            },
                            'p-decimal': { precision: 10, scale: 2 },
                            min_value: { min: 0 }
                        },
                        addRowDataToRule: 'required-if-new-barcode',
                        samples: [12.55,2.75,33.95],
                        convertToNumber: true
                    },
                    {
                        key: 'Description',
                        label: 'Description',
                        canConvertCase: true,
                        hasErrorCalculator: (value, row) => {
                            if (this.isEmpty(value)) {
                                return this.isNewBarcode(row);
                            }
                            return max(value, { length: 200 }) !== true;
                        },
                        warningDefault: '',
                        rules: {
                            'required-if-new-barcode': {
                                isNewBarcode: this.isNewBarcode
                            },
                            max: { length: 200 }
                        },
                        addRowDataToRule: 'required-if-new-barcode',
                        samples: ['"Logo T-Shirt"','"ACADEMY WATERCOLOR"','"HOOD POWERBLEND TWO LOCATION PRINT"']
                    },
                    { 
                        key: 'BrandName',
                        label: 'Brand Name',
                        rules: 'max:50',
                        hasWarningCalculator: value => {
                            return (max(value, { length: 50 }) !== true);
                        },
                        warningDefault: '',
                        samples: ['"2-4001"','"AAA001"','"GM 001"']
                    },
                    {
                        key: 'Variant1',
                        label: 'Variant 1',
                        selectOptionsFilter: () => {return  '1';},
                        hasWarningCalculator: value => {
                            return (
                                value != '' &&
                                value != undefined &&
                                min_value(value, { min: 0 }) !== true
                            );
                        },
                        warningDefault: null,
                        rules: 'min_value:0',
                        samples: ['"XL"','"XXL"','"XXXL"'],
                        dataType: 'productVariants'
                    },
                    {
                        key: 'Variant2',
                        label: 'Variant 2',
                        selectOptionsFilter: () => {return  '2';},
                        hasWarningCalculator: value => {
                            return (
                                value != '' &&
                                value != undefined &&
                                min_value(value, { min: 0 }) !== true
                            );
                        },
                        warningDefault: null,
                        rules: 'min_value:0',
                        samples: ['"Red"','"Gray"','"Navy"'],
                        dataType: 'productVariants'
                    },
                    {
                        key: 'WebsiteDisplayName',
                        label: 'Website Display Name',
                        canConvertCase: true,
                        hasWarningCalculator: value => {
                            return max(value, { length: 400 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:400',
                        samples: ['"Logo T-Shirt"','"ACADEMY WATERCOLOR"','"HOOD POWERBLEND TWO LOCATION PRINT"']
                    },
                    {
                        key: 'WebsiteInventoryTolerance',
                        label: 'Website Inventory Tolerance',
                        hasWarningCalculator: (value) => {
                            return value !== null && value !== '' && !this.isNonNegativeInteger(value);
                        },
                        warningDefault: null,
                        convertToNumber: (value) => {
                            // Convert to number if not null/empty, otherwise return null
                            return (value === null || value === '') ? null : Number(value);
                        },
                        rules: 'integer|min_value:0',
                        samples: [0, '', 5]
                    },
                    {
                        key: 'WebsiteLongDescription',
                        label: 'Website Long Description',
                        canConvertCase: true,
                        hasWarningCalculator: value => {
                            return max(value, { length: 400 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:400',
                        samples: ['"Logo T-Shirt"','"ACADEMY WATERCOLOR"','"HOOD POWERBLEND TWO LOCATION PRINT"']
                    },
                    {
                        key: 'ShowOnlineFlag',
                        label: 'Show Online Flag',
                        hasWarningCalculator: value => {
                            return value !== null && value !== '' && !this.matchesExistingObject(value);
                        },
                        warningDefault: null,
                        dataType: 'yesNoNullOptions',
                        samples: ['"No"', '"Yes"', ''],
                        convertToNull: true
                    },
                    {
                        key: 'WebCatalog',
                        label: 'Web Catalog',
                        hasWarningCalculator: value => {
                            return max(value, { length: 1000 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:1000',
                        samples: ['"9-21 test|Gifts"','"ART SUPPLIES"','"OUTERWEAR"']
                    },
                    {
                        key: 'Serialization',
                        label: 'Serialization',
                        hasWarningCalculator: value => {
                            return !this.matchesExistingObject(value);
                        },
                        warningDefault: false,
                        dataType: 'yesNoOptions',
                        rules: 'min_value:0',
                        samples: ['"Yes"','"No"','"No"']
                    }
                ]
            }
        };

    },
    computed:{
        textBookImportNotes(){   
            let notes = []
            if(this.newBrandNames.length > 0){
                notes.push(`Brand names not found will be added: ${this.newBrandNames.join(', ')}`)
            }  
            if(this.newCatalogNames.length > 0){
                notes.push(`Catalog names not found will not be associated to the product: ${this.newCatalogNames.join(', ')}`  )
            }

            return notes;
        },

        newBrandNames(){
                return [...new Set(this.loadedData.filter(x =>
                    x.BrandName &&
                    !this.selectListOptions.brands.some(s => s.text === x.BrandName) && x.BrandName.length < 50)
                    .map(x => x.BrandName))];
        },

        newCatalogNames()
        {
             
            var splitVal = this.loadedData.filter(x => x.WebCatalog);
            var result=[]; 

            for(let i = 0;i<splitVal.length;i++)
            {
                splitVal[i].WebCatalog.split('|').forEach(element => { 
                    result.push(element)
                }); 
            }  
            
            return [...new Set(result.filter(x => 
                !this.selectListOptions.webCatalogs.some(s => s.text === x))
                .map(x => x))];
        }
    },
    methods: {
        isNewBarcode(rowData) {
            return !this.existingBarcodes.includes(rowData?.Barcode);
        },
        isDuplicatedOnImportBarcode(barcode) {
            return (
                !this.isEmpty(barcode)  &&
                this.loadedData.filter(x => x.Barcode === barcode).length > 1
            );
        }
    }
};
